import {produce} from 'immer';
import {useAtom} from 'jotai';
import {useEffect, useState} from 'react';
import {currentPositionAtom} from '@/map/atom';
import {RegionOptions, SelectedRegion} from '../@types';
import {useQueryRegionGeoData} from '../api';
import {RegionTypes} from '../enums';

export const useWatchPosition = () => {
  const [watchId, setWatchId] = useState<number>();
  const [pos, setPosition] = useAtom(currentPositionAtom);
  const {active, ...position} = pos;

  const setActive = (active: boolean) => {
    setPosition(
      produce((draft) => {
        draft.active = active;
      })
    );
  };

  const watch = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject('현재 위치를 가져올 수 없습니다.');

        return;
      }

      const id = navigator.geolocation.watchPosition(
        (geolocationPosition) => {
          if (!geolocationPosition) {
            return reject('현재 위치를 가져오지 못했습니다.');
          }

          const {latitude, longitude} = geolocationPosition.coords;

          const mapsLatLng = new window.naver.maps.LatLng(latitude, longitude);
          const current = {
            mapsLatLng,
            geolocationPosition,
            active: true,
          };

          setPosition(current);
          resolve(current);
        },
        () => {
          reject('현재 위치를 가져오지 못했습니다.');
        },
        {enableHighAccuracy: true}
      );

      setWatchId(id);
    });

  const clear = () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);

      setWatchId(undefined);
    }
    setPosition({active: false});
  };

  return {
    watchId,
    position,
    active,
    watch,
    clear,
    setActive,
  };
};

export const useRegionGeoData = (
  selectedRegion: SelectedRegion = {},
  options: {
    includeGeometryData?: boolean;
  } = {}
) => {
  const {includeGeometryData} = options;

  const [regions, setRegions] = useState<RegionOptions>({});

  const parentCode = selectedRegion?.[RegionTypes.ctprvn];
  const regionType = !regions[RegionTypes.ctprvn]
    ? RegionTypes.ctprvn
    : selectedRegion?.[RegionTypes.sig]
    ? RegionTypes.emd
    : RegionTypes.sig;

  const {data} = useQueryRegionGeoData({
    includeGeometryData,
    parentCode,
    regionType,
  });

  useEffect(() => {
    if (!parentCode && regions[RegionTypes.sig]) {
      setRegions((prev) => ({
        ...prev,
        [RegionTypes.sig]: undefined,
        [RegionTypes.emd]: undefined,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCode]);

  useEffect(() => {
    if (data) {
      setRegions(
        produce((draft) => {
          draft[regionType] = data;
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    selectedRegion,
    options: regions,
  };
};
